import type { RoleKey } from '../../../../api/users';
import type { RegionOption } from '../../../../shared/regionAccessOptions';
import type { UserRoleId } from '../roleTaxonomy';
import type { UserRow } from '../userViewModel';

import DrawerScrim from './DrawerScrim';
import InviteDrawerBody from './InviteDrawerBody';
import InviteDrawerFooter from './InviteDrawerFooter';
import InviteDrawerHeader from './InviteDrawerHeader';
import { useInviteForm } from './use-invite-form';

interface InviteUserDrawerProps {
  /** Existing users in scope, used to detect imported and duplicate matches. */
  existingUsers: UserRow[];
  /** Roles the viewer may grant. */
  grantableRoles: RoleKey[];
  /** Regions the viewer may assign. */
  assignableRegionIds: string[];
  /** Every top-level region. */
  regionOptions: RegionOption[];
  /** Seeds the form from this user and links the invite to them, so no
   *  duplicate row is created. */
  prefillFrom?: UserRow;
  onClose: () => void;
  /** Called after a successful invite so the page can refetch the user list. */
  onInvited?: () => void;
}

/**
 * Right-side drawer for inviting a user: the operator picks the sign-in method
 * and can link the invite to an existing imported row.
 */
export default function InviteUserDrawer({
  existingUsers,
  grantableRoles,
  assignableRegionIds,
  regionOptions,
  prefillFrom,
  onClose,
  onInvited,
}: InviteUserDrawerProps) {
  const invite = useInviteForm({ existingUsers, prefillFrom, onInvited });
  const assignableRegions = regionOptions.filter((r) => assignableRegionIds.includes(r.id));
  const isLink = invite.match.kind === 'backfill';

  return (
    <DrawerScrim width={460} onClose={onClose} labelledBy="invite-title">
      <InviteDrawerHeader isLink={isLink} onClose={onClose} />
      <InviteDrawerBody
        form={invite.form}
        setForm={invite.setForm}
        match={invite.match}
        nameMatches={invite.nameMatches}
        onPickImported={invite.pickImport}
        onClearPicked={invite.clearPicked}
        grantableRoleIds={grantableRoles as UserRoleId[]}
        regionOptions={assignableRegions}
        requiresRegion={invite.requiresRegion}
        errorMessage={invite.errorMessage}
        successMessage={invite.successMessage}
        linkNotice={invite.linkNotice}
      />
      <InviteDrawerFooter
        canSubmit={invite.canSubmit}
        isLink={isLink}
        confirmLinkExisting={invite.confirmLinkExisting}
        submitting={invite.submitting}
        done={Boolean(invite.successMessage)}
        onCancel={onClose}
        onSubmit={invite.submit}
      />
    </DrawerScrim>
  );
}
